import { usePageInfo, useStateRef } from '@op/hooks/common';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { DBO_TM } from '@op/utils/format';
import { getChartTimeParams } from '@op/utils/time';
import { timeRangeOptions } from '@opm/components/FilterCommon/constants/timeRange';
import { timeRangeTranslate } from '@opm/components/FilterCommon/utils';
import { gotoDownload } from '@opm/pages/Download';
import { getHashtagTableList } from '@opm/services';
import { useUpdateEffect } from 'ahooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { notification } from 'tea-component';
import { SortBy } from 'tea-component/lib/table/addons';
import { useDispatch } from 'umi';

export function useHashtagData({
  filter,
  translateCategory,
  instanceId,
  intl,
}: {
  filter: any;
  timeRangeIsChange?: boolean;
  translateCategory: (text: string, isEn: boolean) => string;
  instanceId: string;
  intl: (id: string) => string;
}) {
  const [sortByRef, sortBy, setSortBy] = useStateRef<SortBy<string>>({
    order: 'desc',
    by: 'tweets_views',
  });
  const [keywordRef, keyword, setKeyword] = useStateRef<string>('');
  const { pageInfo, changePageInfo, resetPageInfo } = usePageInfo({
    defaultPageInfo: { page: 1, page_size: 20 },
  });
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasDataDate, setHasDataDate] = useState({});
  const filterRef = useRef({});
  const [sourceLoading, setSourceLoading] = useState(false);

  function changeSortBy(sortBy: SortBy<string>) {
    setSortBy(sortBy);
    resetPageInfo();
  }

  function changeKeyword(value: string) {
    setKeyword(value);
    resetPageInfo();
  }

  const { wrapperCancelRequest, loading } = useCancelRequestHook();

  function request() {
    if (!filter) {
      return;
    }
    const by = sortByRef.current?.by;
    let order = sortByRef.current?.order;

    if (by === 'rank') {
      order = order === 'desc' ? 'asc' : 'desc';
    }

    const params = {
      ...filter,
      ...pageInfo,
      sort_model: order,
      sort_item: by,
      search_text: keywordRef.current,
    };
    setSourceLoading(true);
    wrapperCancelRequest(getHashtagTableList, params)
      .then(data => {
        const { list, total, has_data_date = '' } = data || {};
        setData(list || []);
        setTotal(total || 0);
        const newHasDataDate = has_data_date
          ? getChartTimeParams(has_data_date, 'daily')
          : {};
        setHasDataDate(newHasDataDate);

        if (filterRef.current.source !== filter.source && has_data_date) {
          viewLatestData(newHasDataDate);
        } else {
          setSourceLoading(false);
        }
      })
      .finally(() => {
        filterRef.current = filter;
      });
  }

  useUpdateEffect(() => {
    resetPageInfo();
  }, [filter]);

  useEffect(() => {
    setData([]);
    request();
  }, [pageInfo]);

  function download() {
    const translateTimeRange = timeRangeTranslate(timeRangeOptions);
    const by = sortByRef.current?.by;
    let order = sortByRef.current?.order;

    if (by === 'rank') {
      order = order === 'desc' ? 'asc' : 'desc';
    }

    const params = {
      ...filter,
      ...pageInfo,
      sort_model: order,
      sort_item: by,
      search_text: keywordRef.current,
    };
    gotoDownload({
      key: 'MarketingHashtagTableDownload',
      fileName: `Hashtag_${translateTimeRange(
        params.time_range,
        true,
      )}_${translateCategory(params.categories[0], true)}_${DBO_TM.timeTitle(
        params,
      )}`,
      filter: params,
    });
  }

  const viewLatestData = useCallback(
    (dateOverride?: Record<string, any>) => {
      const dateToUse = dateOverride || hasDataDate;
      if (!dateToUse || Object.keys(dateToUse).length === 0) return;
      dispatch({
        type: 'opinionMarketingFilter/UPDATE_FILTER',
        payload: dateToUse,
        instanceId,
      });
      if (dateOverride) {
        notification.success({
          description: intl('Switched to the latest date with data'),
        });
      }
    },
    [hasDataDate, instanceId],
  );

  return {
    changeSortBy,
    data,
    total,
    pageInfo,
    changePageInfo,
    sortBy,
    loading: loading || sourceLoading,
    keyword,
    changeKeyword,
    download,
    hasDataDate,
    viewLatestData,
  };
}
